'use-client';
import { useRouter } from 'next/router';
import { useEffect, useState, type FormEvent, type PropsWithChildren } from 'react';
import { createStore } from '@xstate/store';
import { useSelector } from '@xstate/store/react';

import { setCookie, COOKIES } from 'utils/helpers';
import { useI18n } from 'context/i18n';
import { dataLayerHandleEvent } from 'utils/dataLayers';
import { subscribeNewsletter } from 'actions/general';
import { ThankYouMessage } from 'components/thankYouMessage';
import { Dialog, DialogRoot, DialogX } from 'components/ui/dialog';
import { useUser } from 'context/user';
import { Form, Heading } from 'react-aria-components';
import { TextField } from 'components/ui/text-field';
import { getDomainValue, isDomain } from 'utils/domain';
import { parseEmail } from 'utils/validation';
import { ProtectionDataModal } from 'components/protectionDataModal';
import { PrivacyPolicyText } from 'components/legalTexts';
import { Checkbox } from 'components/ui/checkbox';
import { Button } from 'components/ui/button';
import { storage } from 'utils/web-storage';
import { useLocale } from 'hooks';
import { isBlank } from 'utils/StringUtils';
import { getTWSMagazineUrl } from 'utils/tws';
import { DrawerHeader, DrawerRoot } from 'components/ui/drawer';
import { browserToSHA256 } from 'utils/browser';

const store = createStore({
  context: {
    open: false,
    cb: undefined as undefined | (() => Promise<void>),
    popupType: '',
    origin: '',
    href: '',
  },
  on: {
    open: (_, event: { cb?: () => Promise<void>; origin: string; href: string; popupType?: string }) => {
      return { open: true, cb: event.cb, origin: event.origin, href: event.href, popupType: event.popupType ?? 'members_only' };
    },
    close: () => {
      return { open: false, cb: undefined, origin: '', href: '', popupType: '' };
    },
  },
});

function MembersOnlyDialog({ children }: PropsWithChildren<{}>) {
  const { t } = useI18n();
  const isOpen = useSelector(store, (state) => state.context.open);
  const type = useSelector(store, (state) => state.context.popupType);
  const canClose = type !== 'private';

  if (isDomain('camperlab')) {
    return (
      <DrawerRoot
        isOpen={isOpen}
        onOpenChange={(isOpen) => {
          if (canClose && !isOpen) {
            store.send({ type: 'close' });
          }
        }}
        isDismissable={canClose}
      >
        <Dialog className={`flex h-full flex-col p-10 shadow`}>
          <DrawerHeader label={t('ficha.producto', 'modal.members.only.title', 'Members Only')} />
          {children}
        </Dialog>
      </DrawerRoot>
    );
  }

  return (
    <DialogRoot
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        if (canClose && !isOpen) {
          store.send({ type: 'close' });
        }
      }}
      isDismissable={canClose}
    >
      <Dialog className={`flex h-full flex-col p-10 shadow`}>
        {canClose ?
          <DialogX absolute />
        : null}
        <Heading level={2} className="sr-only">
          {t('ficha.producto', 'modal.members.only.title', 'Members Only')}
        </Heading>
        {children}
      </Dialog>
    </DialogRoot>
  );
}

type MembersOnlyModalProps = {
  camperlab?: boolean;
};

type FormState =
  | {
      formState: 'idle' | 'success' | 'pending';
      error: null;
    }
  | {
      formState: 'error';
      error: string;
    };

export function MembersOnlyModal({ camperlab = false }: MembersOnlyModalProps) {
  const { t, popupInfo } = useI18n();
  const { locale, language } = useLocale();
  const { userData } = useUser();
  const router = useRouter();
  const { query } = router;

  const [{ formState, error }, setFormState] = useState<FormState>({ formState: 'idle', error: null });
  const type = useSelector(store, (state) => state.context.popupType);
  const href = useSelector(store, (state) => state.context.href);
  const origin = useSelector(store, (state) => state.context.origin);
  const cb = useSelector(store, (state) => state.context.cb);

  const isEwallet = type === 'ewallet';
  const doubleConfirmation = ['private', 'content'].includes(type);

  useEffect(() => {
    window.showMembersOnlyModal = (origin: string = '', popupType: string = '', href: string = '', cb?: () => Promise<void>) => {
      if (popupType.includes('magazine')) {
        const subscribed = storage.local.get('user_subscribed') === true;
        if (subscribed) {
          const magazineType = popupType.split('-').pop();
          if (!isBlank(href)) {
            window.location.assign(href);
          } else {
            window.open(getTWSMagazineUrl(magazineType, language), '_blank');
          }
          return;
        }
      }
      store.send({ type: 'open', popupType, origin, href, cb });
    };
    return () => {
      window.showMembersOnlyModal = undefined;
    };
  }, [language]);

  const handleSubmit = async (e: FormEvent) => {
    if (formState === 'pending') return;
    e.preventDefault();
    e.stopPropagation();
    setFormState({ formState: 'pending', error: null });
    const values = Object.fromEntries(new FormData(e.target as HTMLFormElement)) as {
      email: string;
      privacy: string;
    };

    const { utm_campaign, utm_source } = query;
    const payload: any = {
      email: values.email,
      profile: locale,
      origin: origin,
      campaign: utm_campaign,
      channel: utm_source,
    };
    if (type === 'ewallet') {
      payload.ewallet = true;
    }
    if (type === 'content' || type === 'private') {
      payload.campaign = 'MembersOnly_Peli_Kilian';
    }
    if (camperlab) {
      payload.lab = true;
    }

    try {
      const result = await subscribeNewsletter(payload);
      setCookie(COOKIES.MEMBER_EMAIL, values.email);
      if (result) {
        dataLayerHandleEvent({
          event: 'acquisition_popup',
          info_type: 'subscription',
          campaign_detail: popupInfo?.title?.toLowerCase() || '',
          page_type: window.gtmType || '',
        });
        const hashedEmail = await browserToSHA256(values?.email?.toLowerCase() ?? '');
        dataLayerHandleEvent({
          event: 'mailShadowed',
          mailSHA256: hashedEmail,
          mailVanilla: values?.email,
        });
        storage.local.set('user_subscribed', true);
        await cb?.();
      }

      if (type === 'ewallet' || type.includes('magazine')) {
        if (href && href?.length > 0) {
          window.location.assign(href);
        } else {
          setFormState({ formState: 'success', error: null });
          return;
        }
      }
      if (type === 'content') {
        setFormState({ formState: 'success', error: null });
        return;
      }
      store.send({ type: 'close' });
    } catch (rqError) {
      console.error(rqError);
      let errorMessage = 'KO: Unexpected Error';
      if (rqError?.data?.errors?.length > 0) {
        errorMessage = rqError?.data?.errors[0].message || 'KO: Unexpected Error';
      }
      setFormState({ formState: 'error', error: errorMessage });
    }
  };

  return (
    <MembersOnlyDialog>
      <div className="mx-auto flex w-[272px] flex-col items-center text-center lab:mx-0 lab:w-full lab:flex-grow lab:items-stretch lab:text-left">
        {formState === 'success' ?
          <ThankYouMessage type={type} className="m-auto text-center lab:m-0 lab:text-left" href={href} />
        : <>
            {isDomain('camperlab') ? null : (
              <img
                src={getDomainValue({
                  camper: '/assets-new/product_availability.svg',
                  nnormal: `/assets-new/${doubleConfirmation ? 'nnormal/kilian-logo' : 'logo-nnormal'}.svg`,
                  camperlab: '/assets-new/product_availability.svg',
                })}
                alt={'Members Only Logo'}
                width={getDomainValue({
                  camper: 80,
                  camperlab: 80,
                  nnormal: 200,
                })}
                height={80}
              />
            )}
            <p className="mb-0 text-2xl lab:text-sm lab:font-bold lab:uppercase lab:lg:text-xs">
              {t('ficha.producto', 'modal.members.only.title', 'Members Only')}
            </p>
            <p
              className="mb-0 mt-4 lab:mt-2 lab:text-sm lab:font-bold lab:lg:text-xs"
              dangerouslySetInnerHTML={{
                __html: t(
                  'ficha.producto',
                  `modal.members.only${doubleConfirmation ? '.content' : ''}.subtitle`,
                  'To buy this exclusive option, please enter your email below',
                ),
              }}
            />

            <Form onSubmit={handleSubmit} className="mt-10 flex w-full flex-col items-stretch gap-4 lab:flex-grow lab:justify-between lab:pb-2.5">
              <div className="flex flex-col items-start gap-4">
                <div className="w-full">
                  <TextField
                    label={t('generico', 'input.email.placeholder', 'Enter your email')}
                    hiddenLabel={isDomain('camper', 'nnormal')}
                    name="email"
                    type="email"
                    className="self-stretch"
                    defaultValue={userData?.account?.email}
                    validate={(value) => {
                      const valid = parseEmail(value).success;
                      return valid ? true : t('validation', 'required.email', 'Please input your email');
                    }}
                    inputProps={{
                      variant: getDomainValue({ camper: 'transparent', nnormal: 'transparent', camperlab: 'default' }),
                      placeholder: getDomainValue({
                        camper: t('generico', 'input.email.placeholder', 'Enter your email'),
                        nnormal: t('generico', 'input.email.placeholder', 'Enter your email'),
                        camperlab: undefined,
                      }),
                    }}
                  />
                </div>
                {!doubleConfirmation ?
                  <div className="lab:my-4">
                    <ProtectionDataModal origin="newsletter" />
                  </div>
                : null}
                <Checkbox name="privacy" isRequired boxClassName="size-5">
                  <PrivacyPolicyText className="text-wrap text-xs lab:max-lg:text-sm" />
                </Checkbox>
                {doubleConfirmation ?
                  <Checkbox name="confirmation" isRequired boxClassName="size-5">
                    <span className="text-xs lab:lg:text-xs">
                      {t('compra.formulario', 'acepto.las')}&nbsp;
                      <a href={'https://www.kilianjornet.cat/en/privacy%E2%80%94policy'} target="_blank" rel="noreferrer">
                        {t('generico', 'privacy.conditions.kilian')}
                      </a>
                      &nbsp;
                      {t('compra.formulario', 'become.member.kilian')}
                    </span>
                  </Checkbox>
                : null}
              </div>

              <p className="text-xs text-error empty:hidden">{error}</p>
              <div className="mt-2 flex items-stretch">
                <Button
                  type="submit"
                  isPending={formState === 'pending'}
                  variant={getDomainValue({ camper: 'outline', nnormal: 'outline', camperlab: 'solid' })}
                  className="w-full"
                >
                  {isEwallet || type.includes('magazine') || type === 'content' || type === 'private' ?
                    t('compra.formulario', 'tws.enviar', 'Send')
                  : `${t('ficha.producto', 'members.only.shop')} ${t('ficha.producto', 'members.only')}`}
                </Button>
              </div>
            </Form>
          </>
        }
      </div>
    </MembersOnlyDialog>
  );
}
